import {messages} from '@smartmixin/i18n';
import React from 'react';
import type {MessageDescriptor, PrimitiveType} from 'react-intl';
import {
  DateTimeConfig,
  formatDate,
  formatDuration,
  hourFormatter,
} from '../Dates';
import {formatBearing, formatBearingSpeech} from '../Units';
import {
  AltitudeSample,
  SunRegime,
  findNextTransition,
  getAltitudeCurve,
  getInfo,
  getLightSummary,
  getLocalMinuteOfDay,
  getPosition,
  getRegime,
} from './astronomy';

/**
 * One day of sun, reduced to the strings and decisions a card renders.
 *
 * A headless presenter: everything from latitude and a timestamp down to
 * formatted text lives here, and everything from formatted text down to pixels
 * lives in each app. `apps/mobile/.../Sun/SunSummary.tsx` and
 * `apps/webapp/.../ephemeride/SunriseAndSunset/index.tsx` were hand
 * transcriptions of one another, which is how three filed defects (#119, #121,
 * #122) came to exist in both copies and how one of them got fixed in only one
 * (FE #170, step 5).
 *
 * What deliberately does NOT cross the seam, and must not start to:
 *
 * - **Layout.** The return value is a bag of named fields, not a list of row
 *   descriptors. Mobile prints three twilight cells and the webapp five behind
 *   a container query; a shared `cells: Row[]` would drag the webapp's `wide`
 *   flag in here, where it means nothing.
 * - **Icons, testIDs, colours.** `chips` and `extremes` carry a stable `id` and
 *   an `accent` boolean; each app maps those to its own testID, icon and theme
 *   token.
 * - **`showBearings`.** The bearings are always computed; the mobile detail
 *   pane, which lists them further down the screen, drops them at render time.
 *
 * `chips` is a list rather than named fields because membership *is* the logic:
 * "daylight left" exists only between sunrise and sunset, and the transition
 * countdown only in a polar regime. Both apps render it with `.map`, so its
 * order is the on-screen order and the tests pin it.
 *
 * The one hook under `utils/` rather than in `packages/core/src/hooks/`,
 * deliberately: it belongs to the `Sun` namespace its callers already import,
 * and the hooks barrel reaches the store and the API client, which is exactly
 * the weight this module is arranged to avoid. What it needs from React is
 * three `useMemo`s and nothing else.
 */

/** Twilight bands are minutes wide; 15-minute sampling resolves them. */
const CURVE_STEP_MINUTES = 15;

const SECONDS_PER_DAY = 86400;

/**
 * The subset of `IntlShape` this needs, matching what `Units.formatBearing` and
 * `Dates.formatDuration` already take. Passed in rather than read from
 * `useIntl()` so that `utils/Sun` keeps no react-intl dependency, and passed
 * alongside `dateTimeConfig` rather than `useDateTimeConfig()` so that it pulls
 * neither constate nor the Redux store into a utils module — the same reason
 * `utils/Charts` takes its config as a parameter.
 */
export type SunIntl = {
  formatMessage: (
    descriptor: MessageDescriptor,
    values?: Record<string, PrimitiveType>,
  ) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
};

export type SunDayViewParams = {
  latitude: number;
  longitude: number;
  timezone: string;
  /** Local midnight of the day being described. */
  startOfDay: number;
  /**
   * Now, as a timestamp, when the day on show is today — the band's marker and
   * the "daylight left" chip both need it, and both must be absent otherwise.
   */
  now: number | null;
  intl: SunIntl;
  dateTimeConfig: DateTimeConfig;
};

export type SunBearing = {
  /** "ENE · 74°", bidi-isolated by `Units.formatBearing`. */
  text: string;
  /** "East-northeast". `null` when the azimuth cannot be binned. */
  speech: string | null;
};

export type SunHeroTime = {
  id: 'sunrise' | 'sunset';
  label: string;
  /** "07:18", in the user's 12/24-hour preference. */
  time: string;
  bearing: SunBearing;
  /**
   * "Sunrise, 07:18, East-northeast" — the whole hero reads as one node rather
   * than three fragments. Named for the ARIA/AAM term, not for either
   * platform's prop: mobile spends it on `accessibilityLabel` and the webapp on
   * `aria-label`.
   */
  accessibleName: string;
};

export type SunExtremeId = 'lowestPoint' | 'highestPoint' | 'brightestMoment';

export type SunExtreme = {
  id: SunExtremeId;
  label: string;
  /**
   * "-2.9°", locale-formatted — the sign is the point of it under a polar
   * night. The two extremes that *are* solar noon (`brightestMoment`,
   * `highestPoint`) carry only their altitude: the twilight row underneath
   * already prints solar noon's time, and the same clock twice, adjacent,
   * under two labels — "Höchststand 12:45" over "SONNENHÖCHSTSTAND 12:45" —
   * was FE #122. The nadir (`lowestPoint`) keeps its time, "00:45 · 2.9°",
   * because no other row has it.
   */
  value: string;
};

/**
 * Two shapes, not one with holes in it. A polar day has no rise or set to
 * print, and the card this replaced showed an em dash for each — or vanished
 * entirely when dawn and dusk were null too.
 */
export type SunHero =
  | {kind: 'times'; sunrise: SunHeroTime; sunset: SunHeroTime}
  | {kind: 'polar'; badge: string; headline: string; extremes: SunExtreme[]};

export type SunTwilightEvent = {
  label: string;
  /** `null` when the event does not happen at this latitude on this date. */
  value: string | null;
};

export type SunChipId = 'dayLength' | 'delta' | 'remaining' | 'transition';

export type SunChip = {
  id: SunChipId;
  text: string;
  /** The one chip that carries the day's headline measure. */
  accent: boolean;
};

export type SunDayView = {
  regime: SunRegime;
  curve: AltitudeSample[];
  /** Minute of the local day, or `null` when the day on show is not today. */
  nowMinute: number | null;
  /**
   * "15:42" — the marker's own time, for the bubble above it, in the user's
   * 12/24-hour preference. `null` exactly when `nowMinute` is: a label with no
   * marker under it, or a marker with no label, are both half a "you are
   * here" (FE #134).
   */
  nowLabel: string | null;
  hero: SunHero;
  twilight: {
    nauticalDawn: SunTwilightEvent;
    dawn: SunTwilightEvent;
    solarNoon: SunTwilightEvent;
    dusk: SunTwilightEvent;
    nauticalDusk: SunTwilightEvent;
  };
  chips: SunChip[];
};

export const useSunDayView = ({
  latitude,
  longitude,
  timezone,
  startOfDay,
  now,
  intl,
  dateTimeConfig,
}: SunDayViewParams): SunDayView => {
  const {formatMessage} = intl;

  // Local midday, not local midnight. suncalc picks the solar day containing
  // the instant it is given, and local midnight in Paris is 22:00 UTC the day
  // before — passing it returned yesterday's sunrise, a minute out.
  const midday = startOfDay + SECONDS_PER_DAY / 2;

  const day = React.useMemo(() => {
    const params = {timestamp: midday, latitude, longitude};
    return {
      info: getInfo(params),
      regime: getRegime(params),
      // One call, so the headline and the delta chip cannot end up measuring
      // different things on a polar boundary day.
      light: getLightSummary(params),
    };
  }, [midday, latitude, longitude]);

  const curve = React.useMemo(
    () =>
      getAltitudeCurve({
        startTimestamp: startOfDay,
        latitude,
        longitude,
        stepMinutes: CURVE_STEP_MINUTES,
        timezone,
      }),
    [startOfDay, latitude, longitude, timezone],
  );

  const {info, regime, light} = day;
  const isPolar = regime === 'polarNight' || regime === 'midnightSun';

  const transition = React.useMemo(
    () =>
      isPolar
        ? findNextTransition({timestamp: midday, latitude, longitude})
        : null,
    [isPolar, midday, latitude, longitude],
  );

  /**
   * The two hero azimuths, resolved once per day rather than once per render.
   *
   * Both cards called `getPosition` inline in their JSX, so a 60-second clock
   * tick re-ran them; nothing in here depends on the minute. `info` comes out
   * of the memo above, so this one only re-runs when the day does.
   *
   * The polar extremes are deliberately NOT here: their timestamps can be null,
   * and resolving them eagerly would mean dereferencing a null position before
   * the filter that drops those rows has run.
   */
  const heroAzimuths = React.useMemo(() => {
    const azimuthAt = (timestamp: number | null) =>
      timestamp === null
        ? null
        : getPosition({timestamp, latitude, longitude}).azimuth;
    return {
      sunrise: azimuthAt(info.sunrise),
      sunset: azimuthAt(info.sunset),
    };
  }, [info, latitude, longitude]);

  const hourFormat = hourFormatter(dateTimeConfig);
  const at = (timestamp: number | null) =>
    timestamp === null ? null : formatDate(timestamp, timezone, hourFormat);

  // Wall-clock minutes, not elapsed seconds: on the 25-hour day that ends DST
  // the latter reaches 1470 and pushes the marker off the end of the band.
  const nowMinute = now === null ? null : getLocalMinuteOfDay(now, timezone);
  const nowLabel = at(now);

  /**
   * "ENE · 74°" plus its spoken form.
   *
   * The degrees alone are a number most people cannot place on the horizon; the
   * compass point is what makes the line mean "over there". The letters are
   * localised — `compass.ENE` is `ONO` in German and `ENE` in French, and the
   * *same* string `ONO` is French for WNW — so they are looked up per locale
   * rather than derived from the English table.
   */
  const heroTime = (
    id: 'sunrise' | 'sunset',
    label: string,
    timestamp: number,
    azimuth: number,
  ): SunHeroTime => {
    const time = formatDate(timestamp, timezone, hourFormat);
    const speech = formatBearingSpeech(azimuth, intl);
    return {
      id,
      label,
      time,
      bearing: {text: formatBearing(azimuth, intl) ?? '', speech},
      // Joined rather than interpolated so an unbinnable azimuth degrades to
      // "Sunrise, 07:18" instead of speaking the word "undefined".
      accessibleName: [label, time, speech].filter(Boolean).join(', '),
    };
  };

  const hero = ((): SunHero => {
    if (!isPolar && info.sunrise !== null && info.sunset !== null) {
      return {
        kind: 'times',
        sunrise: heroTime(
          'sunrise',
          formatMessage(messages.astral.sunrise),
          info.sunrise,
          heroAzimuths.sunrise as number,
        ),
        sunset: heroTime(
          'sunset',
          formatMessage(messages.astral.sunset),
          info.sunset,
          heroAzimuths.sunset as number,
        ),
      };
    }

    const isMidnightSun = regime === 'midnightSun';

    // Under a midnight sun both extremes are above the horizon and both are
    // interesting — the low point is what "night" has become. In a polar night
    // only the high point exists in any useful sense.
    //
    // `withTime` is false on the rows that ARE solar noon: the twilight row
    // below prints its time, and the altitude is the one thing the hero adds
    // (FE #122, option 2). The nadir has no cell below, so it keeps its clock.
    const candidates: {
      id: SunExtremeId;
      key: MessageDescriptor;
      at: number | null;
      withTime: boolean;
    }[] = isMidnightSun
      ? [
          {
            id: 'lowestPoint',
            key: messages.astral.lowestPoint,
            at: info.nadir,
            withTime: true,
          },
          {
            id: 'highestPoint',
            key: messages.astral.highestPoint,
            at: info.solarNoon,
            withTime: false,
          },
        ]
      : [
          {
            id: 'brightestMoment',
            key: messages.astral.brightestMoment,
            at: info.solarNoon,
            withTime: false,
          },
        ];

    return {
      kind: 'polar',
      badge: formatMessage(
        isMidnightSun
          ? messages.astral.midnightSun
          : messages.astral.polarNight,
      ),
      headline: formatMessage(
        isMidnightSun
          ? messages.astral.sunNeverSets
          : messages.astral.noSunriseToday,
      ),
      // Filter first, resolve the altitude second. suncalc returns a solar noon
      // and a nadir at every latitude on every date, so the null rows are
      // unreachable in practice — but reading the altitude before the filter
      // turns a row that used to be silently dropped into a TypeError.
      extremes: candidates
        .filter((entry) => entry.at !== null)
        .map(({id, key, at: when, withTime}) => {
          // One decimal, kept: at Tromsø in mid-December the sun tops out at
          // -2.9°, and rounding that to -3° loses the "how close to sunrise"
          // reading. But through `formatNumber`, never `toFixed` — the latter
          // hardcodes the English separator into all 25 locales, so a German
          // user read "43.7°" beside every other decimal in the app (FE #154).
          const altitude = `${intl.formatNumber(
            getPosition({timestamp: when as number, latitude, longitude})
              .altitude,
            {minimumFractionDigits: 1, maximumFractionDigits: 1},
          )}°`;
          return {
            id,
            label: formatMessage(key),
            value: withTime
              ? `${formatDate(when as number, timezone, hourFormat)} · ${altitude}`
              : altitude,
          };
        }),
    };
  })();

  const twilightEvent = (
    key: MessageDescriptor,
    timestamp: number | null,
  ): SunTwilightEvent => ({label: formatMessage(key), value: at(timestamp)});

  /**
   * Daylight still to come, and only while there is some.
   *
   * Between sunrise and sunset on the day being shown, which is why it needs
   * `now` rather than a minute-of-day: before sunrise "2h02 left" would be a
   * claim about a day that has not started, and it is meaningless on a day the
   * user is only browsing.
   */
  const remaining =
    now !== null &&
    info.sunrise !== null &&
    info.sunset !== null &&
    now >= info.sunrise &&
    now < info.sunset
      ? info.sunset - now
      : null;

  const chips: SunChip[] = [
    {
      id: 'dayLength',
      // The duration leads and the message is a plain noun label.
      // Interpolating the duration into the message would put it after the
      // label in Japanese and Korean, and adds a placeholder to 25 locales for
      // no gain. Labelled by the measure actually used, not by today's regime —
      // they differ on the day a polar night starts or ends.
      text: `${formatDuration(light.seconds, intl)} ${formatMessage(
        light.measure === 'civil'
          ? messages.astral.usableLight
          : messages.astral.dayLength,
      )}`,
      accent: true,
    },
    {
      id: 'delta',
      // Zero through the middle of a polar period is real, not missing.
      text: `${light.delta < 0 ? '−' : '+'}${formatDuration(
        // `formatDuration` clamps negatives to zero, so the magnitude is passed
        // and the sign printed separately — a shortening day is the common case
        // above the equator.
        Math.abs(light.delta),
        intl,
        {precision: 'seconds'},
      )} ${formatMessage(messages.astral.vsYesterday)}`,
      accent: false,
    },
  ];

  if (remaining !== null) {
    chips.push({
      id: 'remaining',
      text: `${formatDuration(remaining, intl)} ${formatMessage(
        messages.astral.ofDaylightLeft,
      )}`,
      accent: false,
    });
  }

  if (transition !== null) {
    chips.push({
      id: 'transition',
      text: formatMessage(
        transition.kind === 'firstSunrise'
          ? messages.astral.firstSunriseIn
          : messages.astral.firstSunsetIn,
        {days: transition.days},
      ),
      accent: false,
    });
  }

  return {
    regime,
    curve,
    nowMinute,
    nowLabel,
    hero,
    twilight: {
      nauticalDawn: twilightEvent(
        messages.astral.nauticalDawn,
        info.nauticalDawn,
      ),
      dawn: twilightEvent(messages.astral.civilDawn, info.dawn),
      solarNoon: twilightEvent(messages.astral.solarNoon, info.solarNoon),
      dusk: twilightEvent(messages.astral.civilDusk, info.dusk),
      nauticalDusk: twilightEvent(
        messages.astral.nauticalDusk,
        info.nauticalDusk,
      ),
    },
    chips,
  };
};
